/**
 * Member RSVP Page
 * Weekly view for members to manage their RSVPs
 */

import { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { useUIStore } from '@/stores/uiStore';
import { useAuthStore } from '@/stores/authStore';
import { WeekSelector } from '@/components/rsvp/WeekSelector';
import { WeeklyGrid, type RSVPSelection } from '@/components/rsvp/WeeklyGrid';
import { type RSVPSize } from '@/components/rsvp/SizeSelector';
import { fetchMenus } from '@/lib/api/menus';
import { fetchRSVPs, bulkUpsertRSVPs } from '@/lib/api/rsvps';
import { fetchMenuRatings, updateMenuRating } from '@/lib/api/menuRatings';
import { fetchSystemSettings } from '@/lib/api/systemSettings';
import {
  getCurrentWeekStart,
  getWeekDates,
  getMonthDates,
  toISODate,
  getWeekRange,
  getMonthRange,
  nextWeek,
  fromISODate,
  formatDate,
  getCutoffDateForWeek,
  isPastDate,
} from '@/lib/utils/dateHelpers';
import {
  isMenuRSVPEligible,
  isMenuRSVPDisabled,
  getCutoffDayLabel,
  findPassedCutoffWeeks,
} from '@/lib/utils/menuHelpers';
import { toggleOptOut } from '@/lib/utils/optOutHelpers';
import { LocationColorBadge } from '@/components/ui/location-color-badge';
import type { MenuRatingSummary, Menu, RSVPWithFeedback, FeedbackItem, SystemSettings } from '@/types/api';
import { isRSVPSizeCode, type RSVPSizeCode } from '@/types/units';

type ViewMode = 'week' | 'month';
const ISO_DATE_REGEX = /^\d{4}-\d{2}-\d{2}$/;
const EMPTY_RATINGS: Map<number, MenuRatingSummary> = new Map();

function parseIsoDateParam(value: string | null): Date | null {
  if (!value || !ISO_DATE_REGEX.test(value)) return null;
  return fromISODate(value);
}

export function RSVP() {
  const { showToast } = useUIStore();
  const { user } = useAuthStore();
  const [searchParams] = useSearchParams();
  const requestedWeekStart = useMemo(
    () => parseIsoDateParam(searchParams.get('weekStart')),
    [searchParams]
  );
  const highlightedDate = useMemo(() => {
    const value = searchParams.get('highlightDate');
    return value && ISO_DATE_REGEX.test(value) ? value : null;
  }, [searchParams]);

  // Navigation state
  const [weekStart, setWeekStart] = useState<Date>(() => requestedWeekStart ?? nextWeek(getCurrentWeekStart()));
  const [viewMode, setViewMode] = useState<ViewMode>('week');

  // Data state
  const [menus, setMenus] = useState<Menu[]>([]);
  const [rsvps, setRsvps] = useState<RSVPWithFeedback[]>([]);
  const [selections, setSelections] = useState<Map<number, RSVPSize>>(new Map());
  const [feedback, setFeedback] = useState<Map<number, string>>(new Map());
  const [initialFeedback, setInitialFeedback] = useState<Map<number, string>>(new Map());
  // null while summaries for the current period are loading or unavailable
  const [ratings, setRatings] = useState<Map<number, MenuRatingSummary> | null>(null);
  const [pendingRatingMenuIds, setPendingRatingMenuIds] = useState<Set<number>>(new Set());
  const [optOuts, setOptOuts] = useState<Map<number, Set<number>>>(new Map());
  const [cutoffSettings, setCutoffSettings] = useState<SystemSettings | null>(null);
  const loadRequestIdRef = useRef(0);
  const pendingRatingSavesRef = useRef(new Map<number, Promise<unknown>>());

  // UI state
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [hasChanges, setHasChanges] = useState(false);

  useEffect(() => {
    if (!requestedWeekStart) return;

    const requestedIso = toISODate(requestedWeekStart);
    setWeekStart((current) => (
      toISODate(current) === requestedIso ? current : requestedWeekStart
    ));
    setViewMode('week');
  }, [requestedWeekStart]);

  // Computed values
  const displayDates = useMemo(() => {
    return viewMode === 'month' ? getMonthDates(weekStart) : getWeekDates(weekStart);
  }, [weekStart, viewMode]);

  // Create a map of date -> menu for quick lookup
  const menusByDate = useMemo(() => {
    const map = new Map<string, Menu>();
    for (const menu of menus) {
      map.set(menu.date, menu);
    }
    return map;
  }, [menus]);

  const cutoffByMenuId = useMemo(() => {
    const map = new Map<number, boolean>();
    for (const menu of menus) {
      map.set(menu.id, !!menu.rsvpCutoffPassed);
    }
    return map;
  }, [menus]);

  const weekCutoffPassed = useMemo(() => {
    return displayDates.some((date) => {
      const menu = menusByDate.get(toISODate(date));
      return menu?.rsvpCutoffPassed;
    });
  }, [displayDates, menusByDate]);

  // Unique week-start Mondays covering the displayed range; cached so the cutoff
  // memos below don't each re-walk displayDates and re-call date-fns per day.
  const uniqueWeekStarts = useMemo(() => {
    const seen = new Map<string, Date>();
    for (const date of displayDates) {
      const { start } = getWeekRange(date);
      const key = toISODate(start);
      if (!seen.has(key)) seen.set(key, start);
    }
    return Array.from(seen.values()).sort((a, b) => a.getTime() - b.getTime());
  }, [displayDates]);

  const lastPassedCutoffWeekStart = useMemo(() => {
    if (viewMode !== 'month') return null;
    const passed = findPassedCutoffWeeks(uniqueWeekStarts, menusByDate);
    return passed.length > 0 ? passed[passed.length - 1] : null;
  }, [uniqueWeekStarts, menusByDate, viewMode]);

  const cutoffDayLabel = cutoffSettings ? getCutoffDayLabel(cutoffSettings.cutoffDay) : null;
  const cutoffDateLabel = useMemo(() => {
    if (!cutoffSettings || uniqueWeekStarts.length === 0) return null;
    const cutoffDates = uniqueWeekStarts.map((ws) =>
      getCutoffDateForWeek(ws, cutoffSettings.cutoffDay)
    );
    // Prefer the next upcoming cutoff; fall back to the latest one if all have passed.
    const nextCutoff =
      cutoffDates.find((d) => !isPastDate(d)) ?? cutoffDates[cutoffDates.length - 1];
    return formatDate(nextCutoff, 'EEEE, MMM d');
  }, [cutoffSettings, uniqueWeekStarts]);

  const pickupLocationName = user?.pickupLocationName ?? null;
  const pickupLocationColorCode = user?.pickupLocationColorCode ?? null;
  const thaaliNumber = user?.thaaliNumber ?? null;
  const highlightedDateInView = useMemo(() => (
    highlightedDate && displayDates.some((date) => toISODate(date) === highlightedDate)
      ? highlightedDate
      : null
  ), [displayDates, highlightedDate]);

  // Load menus and RSVPs for the current period
  const loadData = useCallback(async () => {
    const loadRequestId = ++loadRequestIdRef.current;
    try {
      setLoading(true);
      setRatings(null);

      // Get date range for API based on view mode
      const { start, end } = viewMode === 'month'
        ? getMonthRange(weekStart)
        : getWeekRange(weekStart);
      const startDate = toISODate(start);
      const endDate = toISODate(end);

      const [menusData, settingsResponse] = await Promise.all([
        fetchMenus(startDate, endDate),
        fetchSystemSettings().catch(() => null),
      ]);

      // Only show published menus to members
      const publishedMenus = menusData.filter((m) => m.status === 'PUBLISHED');

      // Get menu IDs for feedback lookup (enables feedback without RSVP)
      const menuIds = publishedMenus.map((m) => m.id);

      if (loadRequestId !== loadRequestIdRef.current) return;

      // Commit the fetched menus now so an RSVP fetch failure below still shows
      // this period's menus; menu-dependent state is cleared alongside so stale
      // selections can never pair with them. (The loading spinner covers the gap.)
      setCutoffSettings(settingsResponse?.cutoff ?? null);
      setMenus(publishedMenus);
      setRsvps([]);
      setSelections(new Map());
      setOptOuts(new Map());
      setFeedback(new Map());
      setInitialFeedback(new Map());
      setHasChanges(false);

      // Fetch rating summaries only after in-flight rating saves settle, so
      // the summaries can never be a pre-save snapshot that reverts a saved rating.
      const ratingRequest = menuIds.length > 0
        ? (async () => {
            const pendingSaves = Array.from(pendingRatingSavesRef.current.values());
            if (pendingSaves.length > 0) await Promise.allSettled(pendingSaves);
            return fetchMenuRatings(menuIds);
          })().catch(() => null)
        : Promise.resolve(null);

      // Fetch RSVPs with feedback for all menu IDs
      // Scope to current user; without memberId, admin/caterer would receive all members' RSVPs.
      const { rsvps: rsvpsData, feedbackByMenuId } = await fetchRSVPs({
        includeFeedback: true,
        menuIds,
        memberId: user?.id,
      });

      // Filter RSVPs to only those for menus in this period
      const weekMenuIds = new Set(menuIds);
      const weekRsvps = rsvpsData.filter((r) => weekMenuIds.has(r.menuId));

      // Initialize selections from existing RSVPs only.
      // Menus without an RSVP show no selection until the user picks one and saves.
      const initialSelections = new Map<number, RSVPSize>();

      for (const rsvp of weekRsvps) {
        if (rsvp.status === 'ACTIVE') {
          initialSelections.set(rsvp.menuId, rsvp.size);
        } else if (rsvp.status === 'CANCELLED') {
          initialSelections.set(rsvp.menuId, 'SKIP');
        }
      }

      // Initialize opt-outs from existing ACTIVE RSVPs
      const initialOptOuts = new Map<number, Set<number>>();
      for (const rsvp of weekRsvps) {
        if (rsvp.status === 'ACTIVE' && rsvp.optedOutMenuRecipeIds?.length > 0) {
          initialOptOuts.set(rsvp.menuId, new Set(rsvp.optedOutMenuRecipeIds));
        }
      }
      // Initialize feedback from feedbackByMenuId (includes feedback without RSVP)
      const feedbackMap = new Map<number, string>();
      if (feedbackByMenuId) {
        for (const [menuIdStr, text] of Object.entries(feedbackByMenuId)) {
          feedbackMap.set(Number(menuIdStr), text);
        }
      }
      if (loadRequestId !== loadRequestIdRef.current) return;

      setRsvps(weekRsvps);
      setSelections(initialSelections);
      setOptOuts(initialOptOuts);
      setFeedback(feedbackMap);
      setInitialFeedback(new Map(feedbackMap)); // Clone for change detection
      setHasChanges(false);

      if (menuIds.length === 0) {
        setRatings(new Map());
      } else {
        void ratingRequest.then((ratingData) => {
          if (loadRequestId !== loadRequestIdRef.current) return;
          if (!ratingData?.ratingsByMenuId) {
            // Degrade gracefully: leave the controls usable with zeroed summaries —
            // a save returns the authoritative summary for whichever menu is used.
            setRatings(new Map());
            showToast('Could not load menu ratings', 'error');
            return;
          }
          setRatings(new Map(
            Object.entries(ratingData.ratingsByMenuId)
              .map(([id, value]) => [Number(id), value] as const)
          ));
        });
      }
    } catch (error) {
      if (loadRequestId === loadRequestIdRef.current) {
        showToast(
          error instanceof Error ? error.message : 'Failed to load data',
          'error'
        );
      }
    } finally {
      if (loadRequestId === loadRequestIdRef.current) {
        setLoading(false);
      }
    }
  }, [weekStart, viewMode, showToast, user?.id]);


  // Load data when week changes
  useEffect(() => {
    loadData();
  }, [loadData]);

  // Handle selection change
  const handleSelectionChange = useCallback((menuId: number, size: RSVPSize) => {
    setSelections((prev) => {
      const next = new Map(prev);
      next.set(menuId, size);
      return next;
    });
    setHasChanges(true);
  }, []);

  // Handle feedback change
  const handleFeedbackChange = useCallback((menuId: number, text: string) => {
    setFeedback((prev) => {
      const next = new Map(prev);
      if (text) {
        next.set(menuId, text);
      } else {
        next.delete(menuId); // Remove empty feedback
      }
      return next;
    });
    setHasChanges(true);
  }, []);

  const handleRatingChange = useCallback((menuId: number, clicked: number) => {
    if (pendingRatingSavesRef.current.has(menuId)) return;
    const loadRequestId = loadRequestIdRef.current;

    const previous = ratings?.get(menuId) ?? { averageRating: null, ratingCount: 0, currentUserRating: null };
    // Clicking the current rating clears it, matching the old toggle-off behavior.
    const nextRating: number | null = previous.currentUserRating === clicked ? null : clicked;
    // Optimistic average: reconstruct the sum, swap this member's contribution.
    let sum = (previous.averageRating ?? 0) * previous.ratingCount;
    let count = previous.ratingCount;
    if (previous.currentUserRating !== null) { sum -= previous.currentUserRating; count--; }
    if (nextRating !== null) { sum += nextRating; count++; }
    const optimistic: MenuRatingSummary = {
      averageRating: count > 0 ? Math.round((sum / count) * 100) / 100 : null,
      ratingCount: count,
      currentUserRating: nextRating,
    };
    setPendingRatingMenuIds((current) => new Set(current).add(menuId));
    setRatings((current) => (current ? new Map(current).set(menuId, optimistic) : current));

    const save = (async () => {
      try {
        const saved = await updateMenuRating(menuId, nextRating);
        // After a period reload, loadData's summaries (fetched once this save
        // settles) are the source of truth — drop the stale continuation.
        if (loadRequestId !== loadRequestIdRef.current) return;
        setRatings((current) => (current ? new Map(current).set(menuId, saved) : current));
      } catch (error) {
        if (loadRequestId !== loadRequestIdRef.current) return;
        setRatings((current) => (current ? new Map(current).set(menuId, previous) : current));
        showToast(error instanceof Error ? error.message : 'Failed to save rating', 'error');
      } finally {
        pendingRatingSavesRef.current.delete(menuId);
        setPendingRatingMenuIds((current) => {
          const next = new Set(current);
          next.delete(menuId);
          return next;
        });
      }
    })();
    pendingRatingSavesRef.current.set(menuId, save);
  }, [ratings, showToast]);

  // Handle opt-out change
  const handleOptOutChange = useCallback((menuId: number, menuRecipeId: number, included: boolean) => {
    setOptOuts((prev) => {
      const next = new Map(prev);
      const menuOptOuts = toggleOptOut(prev.get(menuId) || new Set(), menuRecipeId, included);
      if (menuOptOuts.size > 0) {
        next.set(menuId, menuOptOuts);
      } else {
        next.delete(menuId);
      }
      return next;
    });
    setHasChanges(true);
  }, []);

  // Apply size to all editable menus
  const handleApplyToAll = useCallback(
    (size: RSVPSizeCode) => {
      setSelections((prev) => {
        const next = new Map(prev);
        let didChange = false;
        for (const menu of menus) {
          if (!isMenuRSVPDisabled(menu)) {
            if (next.get(menu.id) !== size) {
              next.set(menu.id, size);
              didChange = true;
            }
          }
        }
        if (didChange) {
          setHasChanges(true);
        }
        return next;
      });
    },
    [menus]
  );

  // Submit RSVPs and feedback
  const handleSubmit = async () => {
    try {
      setSubmitting(true);

      // Build selection array (exclude SKIP and null), include opt-outs
      const selectionsToSubmit = Array.from(selections.entries())
        .filter(([menuId, size]) => {
          if (!isRSVPSizeCode(size)) return false;
          const menu = menus.find((item) => item.id === menuId);
          return menu && isMenuRSVPEligible(menu);
        })
        .map(([menuId, size]) => ({
          menuId,
          size: size as RSVPSizeCode,
          optedOutMenuRecipeIds: Array.from(optOuts.get(menuId) || []),
        }));

      // Build feedback array - include all feedback (current and initial) for delta detection
      const allFeedbackMenuIds = new Set([...feedback.keys(), ...initialFeedback.keys()]);
      const feedbackToSubmit: FeedbackItem[] = Array.from(allFeedbackMenuIds).map((menuId) => ({
        menuId,
        feedbackText: feedback.get(menuId) || '',
      }));

      await bulkUpsertRSVPs(
        selectionsToSubmit,
        feedbackToSubmit,
        menus.map((menu) => menu.id)
      );

      showToast('RSVPs and feedback updated successfully', 'success');
      setHasChanges(false);

      // Reload data to get fresh state
      await loadData();
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Failed to update RSVPs';
      showToast(
        message === 'RSVP_CUTOFF_PASSED'
          ? 'RSVP cutoff has passed for this week (Pacific time)'
          : message,
        'error'
      );
    } finally {
      setSubmitting(false);
    }
  };

  // Count selected menus with active thaali selections
  const selectedCount = useMemo(() => {
    return Array.from(selections.values()).filter(isRSVPSizeCode).length;
  }, [selections]);

  return (
    <div className="container mx-auto p-4 md:p-8 max-w-4xl">
      {/* Header */}
      <div className="mb-6">
        <h1 className="text-2xl font-bold mb-1">Weekly RSVP</h1>
        <p className="text-muted-foreground">
          Select your meal preferences for the {viewMode}
        </p>
        {pickupLocationName && (
          <div className="flex flex-wrap items-center gap-2 text-sm text-muted-foreground">
            <span>Pickup location:</span>
            <LocationColorBadge colorCode={pickupLocationColorCode} className="rounded-full px-2.5 font-medium">
              {pickupLocationName}
            </LocationColorBadge>
            {thaaliNumber && (
              <LocationColorBadge colorCode={pickupLocationColorCode} className="rounded-full px-2.5 font-medium">
                #{thaaliNumber}
              </LocationColorBadge>
            )}
            <Badge
              variant="outline"
              className={
                user?.mithaas === false
                  ? 'rounded-full bg-yellow-100 border-yellow-300 text-yellow-900 font-medium'
                  : 'rounded-full'
              }
            >
              Mithaas: {user?.mithaas === false ? 'No' : 'Yes'}
            </Badge>
          </div>
        )}
      </div>

      {/* Controls: Week/Month Toggle */}
      <div className="flex items-center justify-between mb-4">
        <div className="flex gap-2">
          <Button
            variant={viewMode === 'week' ? 'default' : 'outline'}
            size="sm"
            onClick={() => setViewMode('week')}
          >
            Week
          </Button>
          <Button
            variant={viewMode === 'month' ? 'default' : 'outline'}
            size="sm"
            onClick={() => setViewMode('month')}
          >
            Month
          </Button>
        </div>
        <Badge variant="secondary">
          {selectedCount} meal{selectedCount !== 1 ? 's' : ''} selected
        </Badge>
      </div>

      {/* Week/Month Navigation */}
      <WeekSelector
        weekStart={weekStart}
        onWeekChange={setWeekStart}
        disabled={submitting}
        navigationMode={viewMode}
      />

      {cutoffSettings && cutoffDayLabel && (
        <Card className="mb-4 p-4 bg-muted/30 border-muted">
          <div className="space-y-1">
            <p className="text-sm text-muted-foreground">
              RSVP closes {cutoffDateLabel ?? cutoffDayLabel} at {cutoffSettings.cutoffTime} (Pacific)
            </p>
            {weekCutoffPassed && viewMode === 'week' && (
              <p className="text-sm font-medium text-foreground">Cutoff passed for this week</p>
            )}
            {viewMode === 'month' && lastPassedCutoffWeekStart && (
              <p className="text-sm font-medium text-foreground">
                Cutoff passed for week of {formatDate(lastPassedCutoffWeekStart, 'MMM d, yyyy')}
              </p>
            )}
          </div>
        </Card>
      )}

      {/* Content */}
      {loading ? (
        <div className="flex items-center justify-center py-12">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
        </div>
      ) : menus.length === 0 ? (
        <Card className="p-12 text-center">
          <p className="text-muted-foreground">
            No menus published for this {viewMode} yet.
          </p>
          <p className="text-sm text-muted-foreground mt-2">
            Check back later or navigate to another {viewMode}.
          </p>
        </Card>
      ) : (
        <>
          {/* Menu Grid */}
          <WeeklyGrid
            weekDates={displayDates}
            menus={menusByDate}
            selections={selections}
            feedback={feedback}
            ratings={ratings ?? EMPTY_RATINGS}
            ratingsDisabled={ratings === null}
            pendingRatingMenuIds={pendingRatingMenuIds}
            optOuts={optOuts}
            cutoffByMenuId={cutoffByMenuId}
            highlightedDate={highlightedDateInView}
            onSelectionChange={handleSelectionChange}
            onFeedbackChange={handleFeedbackChange}
            onRatingChange={handleRatingChange}
            onOptOutChange={handleOptOutChange}
            onApplyToAll={handleApplyToAll}
            onSubmit={handleSubmit}
            hasChanges={hasChanges}
            submitting={submitting}
            disabled={submitting}
          />
        </>
      )}
    </div>
  );
}
